import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ChevronRight, Zap } from "lucide-react";
import SameDayBadge from "../SameDayBadge";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_bulk-print-exchange/artifacts/uf37wuq4_image.png";

export function MobileHero() {
  return (
    <section className="relative bg-kolt-inkwell text-white overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 blueprint-grid-dense opacity-[0.08] pointer-events-none" />
      <div className="relative px-5 pt-8 pb-6">
        <div className="flex items-center gap-3 mb-4">
          <span className="h-px w-8 bg-kolt-orange" />
          <span className="font-mono text-[10px] tracking-[0.3em] font-bold uppercase text-kolt-orange">
            Houston · Ships nationwide
          </span>
        </div>
        <h1 className="font-display font-black text-[2.35rem] leading-[0.98] uppercase tracking-tight mb-3">
          Custom printing you&apos;ll actually love.
        </h1>
        <p className="text-sm text-white/70 leading-relaxed mb-6 max-w-sm">
          Business cards, labels, banners, books and stationery — every job hand-proofed by a designer. Order before 11 AM CT for same-day dispatch.
        </p>
        <div className="grid grid-cols-2 gap-2.5 mb-5">
          <Link
            to="/shop"
            className="flex items-center justify-center gap-2 bg-kolt-orange active:bg-kolt-orange-hover text-white font-mono text-[11px] tracking-[0.22em] font-bold uppercase py-3.5"
            data-testid="hero-shop-cta-mobile"
          >
            Shop products <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/quote"
            className="flex items-center justify-center gap-2 border border-white/40 active:bg-white active:text-kolt-inkwell text-white font-mono text-[11px] tracking-[0.22em] font-bold uppercase py-3.5"
            data-testid="hero-quote-cta-mobile"
          >
            Get a quote <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
        <Link
          to="/quote?rush=1"
          className="flex flex-col items-center gap-0.5 bg-[#0B0B0B] border border-[#B87333]/50 font-mono tracking-[0.2em] font-black uppercase py-2.5 mb-3 shadow-[4px_4px_0_rgba(184,115,51,0.85)]"
          data-testid="hero-rush-cta-mobile"
        >
          <span className="flex items-center gap-2 text-[11px] text-copper-foil">
            <Zap className="w-3.5 h-3.5 text-[#E8B587]" aria-hidden /> Rush Job?
          </span>
          <span className="text-[9px] tracking-[0.12em] font-normal normal-case text-[#E8B587]/90">
            Share the specs and we&rsquo;ll do our best to save the day!
          </span>
        </Link>
        <SameDayBadge testid="hero-same-day-cta" />
      </div>
      <div className="relative aspect-[16/9] overflow-hidden border-t border-white/10">
        <img
          src={HERO_IMG}
          alt="Foil-stamped business cards, calipers and blueprint drawings on the Kolt Printing press floor"
          className="w-full h-full object-cover object-right"
          loading="eager"
          data-testid="hero-image"
        />
      </div>
    </section>
  );
}

export function MobileCategoryList({ categories }) {
  const BUNDLED = ["roll-labels", "digital-print", "offset-print", "retractables-banners", "booklets-books", "presentation-folders", "custom-roll-labels"];
  return (
    <ul className="divide-y divide-slate-200 border-y border-slate-200 -mx-4" data-testid="mobile-category-list">
      {categories.map((cat, i) => {
        const Icon = cat.icon;
        const img = BUNDLED.includes(cat.slug) ? `/categories/${cat.slug}.webp` : cat.image || "";
        return (
          <li key={cat.slug}>
            <Link
              to={`/shop/${cat.slug}`}
              className="flex items-center gap-4 px-4 py-3 bg-white active:bg-slate-50"
              data-testid={`category-card-${cat.slug}`}
            >
              <div className="w-16 h-16 shrink-0 bg-slate-100 overflow-hidden grid place-items-center">
                {img ? (
                  <img src={img} alt={cat.name} className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                ) : (
                  Icon && <Icon className="w-7 h-7 text-slate-300" strokeWidth={1} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-slate-400 mb-0.5">
                  {String(i + 1).padStart(2, "0")} · {cat.spec}
                </div>
                <div className="font-display font-black text-base text-kolt-slate-dark uppercase leading-tight">{cat.name}</div>
                <div className="text-xs text-slate-500 truncate mt-0.5">{cat.short}</div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400 shrink-0" />
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
